import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  DogServiceEnrollment,
  Service,
  CreateDogServiceEnrollmentInput,
} from "../lib/api";

interface StatusBadgeProps {
  status: DogServiceEnrollment["status"];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const styles: Record<DogServiceEnrollment["status"], string> = {
    active: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-gray-100 text-gray-500",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

interface EnrollmentCardProps {
  enrollment: DogServiceEnrollment;
  serviceName: string;
  onIncrement: () => void;
  onComplete: () => void;
  isIncrementing: boolean;
  isCompleting: boolean;
}

function EnrollmentCard({
  enrollment,
  serviceName,
  onIncrement,
  onComplete,
  isIncrementing,
  isCompleting,
}: EnrollmentCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-gray-900">{serviceName}</p>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={enrollment.status} />
            <span className="text-sm text-gray-500">
              {enrollment.appointmentsCompleted} appointments completed
            </span>
          </div>
          {enrollment.notes && (
            <p className="text-sm text-gray-600 mt-1">{enrollment.notes}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}
          </p>
        </div>
        {enrollment.status === "active" && (
          <div className="flex gap-2">
            <button
              onClick={onIncrement}
              disabled={isIncrementing}
              className="px-2 py-1 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 disabled:opacity-50"
            >
              {isIncrementing ? "..." : "+1 Session"}
            </button>
            <button
              onClick={onComplete}
              disabled={isCompleting}
              className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
            >
              {isCompleting ? "..." : "Complete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface StartEnrollmentModalProps {
  dogId: string;
  programServices: Service[];
  onClose: () => void;
  onSuccess: () => void;
}

function StartEnrollmentModal({
  dogId,
  programServices,
  onClose,
  onSuccess,
}: StartEnrollmentModalProps) {
  const [selectedService, setSelectedService] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateDogServiceEnrollmentInput) =>
      api.dogServiceEnrollments.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to start enrollment");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedService) {
      setError("Please select a program");
      return;
    }

    createMutation.mutate({
      dogId,
      appointmentTypeId: selectedService,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Start Enrollment</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="programSelect"
              className="block text-sm font-medium text-gray-700"
            >
              Program
            </label>
            <select
              id="programSelect"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            >
              <option value="">Select a program...</option>
              {programServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} (${parseFloat(service.price).toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="enrollmentNotes"
              className="block text-sm font-medium text-gray-700"
            >
              Notes
            </label>
            <textarea
              id="enrollmentNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              placeholder="Optional notes about this enrollment"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Starting..." : "Start Enrollment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DogEnrollmentsProps {
  dogId: string;
  dogName: string;
}

export function DogEnrollments({ dogId, dogName: _dogName }: DogEnrollmentsProps) {
  const queryClient = useQueryClient();
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [incrementingId, setIncrementingId] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);

  // Fetch enrollments for this dog
  const { data: enrollmentsData, isLoading: isLoadingEnrollments } = useQuery({
    queryKey: ["dogServiceEnrollments", dogId],
    queryFn: () => api.dogServiceEnrollments.list({ dogId }),
  });

  // Fetch services to get names and for the enrollment modal
  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.services.list({ limit: 100 }),
  });

  const incrementMutation = useMutation({
    mutationFn: (id: string) => api.dogServiceEnrollments.increment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dogServiceEnrollments", dogId],
      });
    },
    onSettled: () => {
      setIncrementingId(null);
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => api.dogServiceEnrollments.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dogServiceEnrollments", dogId],
      });
    },
    onSettled: () => {
      setCompletingId(null);
    },
  });

  const handleIncrement = (id: string) => {
    setIncrementingId(id);
    incrementMutation.mutate(id);
  };

  const handleComplete = (id: string) => {
    setCompletingId(id);
    completeMutation.mutate(id);
  };

  const handleEnrollmentSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["dogServiceEnrollments", dogId],
    });
  };

  const enrollments = enrollmentsData?.data ?? [];
  const services = servicesData?.data ?? [];
  const serviceMap = new Map(services.map((s) => [s.id, s]));
  const programServices = services.filter((s) => s.isProgram && s.isActive);

  const activeEnrollments = enrollments.filter((e) => e.status === "active");
  const pastEnrollments = enrollments.filter((e) => e.status !== "active");

  if (isLoadingEnrollments) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-sm text-gray-500 animate-pulse">
          Loading enrollments...
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Enrollments</h4>
        {programServices.length > 0 && (
          <button
            onClick={() => setShowEnrollModal(true)}
            className="text-xs text-amber-600 hover:text-amber-700 font-medium"
          >
            + Start Enrollment
          </button>
        )}
      </div>

      {activeEnrollments.length === 0 && pastEnrollments.length === 0 ? (
        <p className="text-sm text-gray-500">No enrollments yet.</p>
      ) : (
        <div className="space-y-3">
          {activeEnrollments.length > 0 && (
            <div className="space-y-2">
              {activeEnrollments.map((enrollment) => (
                <EnrollmentCard
                  key={enrollment.id}
                  enrollment={enrollment}
                  serviceName={
                    serviceMap.get(enrollment.appointmentTypeId)?.name ??
                    "Unknown Service"
                  }
                  onIncrement={() => handleIncrement(enrollment.id)}
                  onComplete={() => handleComplete(enrollment.id)}
                  isIncrementing={incrementingId === enrollment.id}
                  isCompleting={completingId === enrollment.id}
                />
              ))}
            </div>
          )}

          {pastEnrollments.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                Past Enrollments
              </p>
              <div className="space-y-2">
                {pastEnrollments.map((enrollment) => (
                  <EnrollmentCard
                    key={enrollment.id}
                    enrollment={enrollment}
                    serviceName={
                      serviceMap.get(enrollment.appointmentTypeId)?.name ??
                      "Unknown Service"
                    }
                    onIncrement={() => handleIncrement(enrollment.id)}
                    onComplete={() => handleComplete(enrollment.id)}
                    isIncrementing={incrementingId === enrollment.id}
                    isCompleting={completingId === enrollment.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showEnrollModal && (
        <StartEnrollmentModal
          dogId={dogId}
          programServices={programServices}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={handleEnrollmentSuccess}
        />
      )}
    </div>
  );
}
